import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { Contracts } from "@/api/contracts";
import { Empty } from "@/components/ui";
import { ContractRow, ContractTrack, StatusPill } from "./ContractBits";
import NewContractDialog from "./NewContractDialog";

/** An account's contracts, as on the Contracts page. */
export function AccountContracts({ accountId }: { accountId: string }) {
  const [creating, setCreating] = useState(false);
  const query = useQuery({
    queryKey: ["contracts", { accountId }],
    queryFn: () => Contracts.list({ account_id: accountId }),
  });
  const rows = query.data ?? [];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className="btn" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" /> New contract
        </button>
      </div>
      <div className="panel">
        {query.isLoading ? (
          <p className="p-6 text-md text-mut-500">Loading…</p>
        ) : rows.length === 0 ? (
          <Empty title="No contracts with this account yet" />
        ) : (
          <ul className="divide-y divide-fg/[0.05]">
            {rows.map((c) => (
              <ContractRow key={c.id} contract={c} showAccount={false} />
            ))}
          </ul>
        )}
      </div>
      {creating && <NewContractDialog accountId={accountId} onClose={() => setCreating(false)} />}
    </div>
  );
}

/** A deal's contracts, compact for the deal panel's side rail. */
export function DealContracts({
  dealId,
  accountId,
  onNavigate,
}: {
  dealId: string;
  accountId: string | null;
  onNavigate?: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const query = useQuery({
    queryKey: ["contracts", { dealId }],
    queryFn: () => Contracts.list({ deal_id: dealId }),
  });
  const rows = query.data ?? [];
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="section-title">Contracts</h3>
        <button className="btn btn-ghost !px-2 !py-1 text-sm" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" /> New
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-mut-500">
          {query.isLoading ? "Loading…" : "None yet. Start one when this deal reaches paperwork."}
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((c) => (
            <li key={c.id}>
              <Link
                to={`/contracts/${c.id}`}
                onClick={onNavigate}
                className="block space-y-2 rounded-xl border border-fg/[0.08] bg-ink-900 px-3 py-2.5 hover:border-fg/[0.16]"
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-md font-medium">{c.title}</span>
                  <StatusPill status={c.status} />
                </div>
                <ContractTrack status={c.status} compact />
              </Link>
            </li>
          ))}
        </ul>
      )}
      {creating && (
        <NewContractDialog
          dealId={dealId}
          accountId={accountId}
          onClose={() => setCreating(false)}
        />
      )}
    </section>
  );
}
